import { jwtDecode } from 'jwt-decode';
import { inject, Injectable } from '@angular/core';
import {
  LOCAL_LANG,
  LOCAL_STORAGE_EXT_TOKEN,
  LOCAL_STORAGE_ID_TOKEN,
  LOCAL_STORAGE_ID_TOKEN_CLAIMS,
  LOCAL_STORAGE_INT_TOKEN,
  LOCAL_STORAGE_REFRESH_TOKEN,
  LOCAL_STORAGE_TOKEN,
} from '../constants/constants';
import { BehaviorSubject, Observable } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import { RoutePaths } from '../constants/route-paths';
import { ModalData } from '../../components/shared/type/modal';
import { ModalService } from '../../components/shared/services/modal.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { IdTokenClaims, Roles, tab, UserSessionData } from '../types/session-data';
import { SpinnerService } from './spinner.service';

@Injectable({
  providedIn: 'root',
})
export class DataSessionService {
  token: string = '';
  refreshToken: string = '';
  isRefreshTokenUsed: boolean = false;
  userData!: UserSessionData | null;
  validRoles: Roles[] = ['responsable_contenido', 'administrador', 'cliente'];
  userExternalRole: Roles[] = ['external'];
  baseUrl: string = environment.apiUrl;

  private _isLoggedIn = new BehaviorSubject<boolean>(this.hasValidToken());
  isLoggedIn$ = this._isLoggedIn.asObservable();

  closeAutoModalData = (
    removeToken: () => void,
): ModalData => {
    return {
      icon: 'icon-circulo-error',
      title: 'logged-header.close-modal.title-close-auto',
      subtitle: '',
      dismissButton: {label: '',
        action: () => removeToken(),
      },
      rigthButton: {label: 'logged-header.close-modal.btn-accept',
        action: () => removeToken(),
      }
    };
};

recaptchaErrorModalData = (
  action: () => void,
): ModalData => {
  return {
    icon: 'icon-circulo-error',
    title: 'error.recaptcha-modal',
    subtitle: '',
    dismissButton: {label: '',
      action: () => action(),
    },
    rigthButton: {label: 'logged-header.close-modal.btn-accept',
      action: () => action(),
    }
  };
};

  private readonly _http = inject(HttpClient);
  private readonly _msalService = inject(MsalService);
  private readonly _router = inject(Router);
  private readonly _modalService = inject(ModalService);
  private readonly _spinnerService = inject(SpinnerService);

  private roleTabAccess: Record<Roles, tab[]> = {
    administrador: ['management', 'content', 'my-global'],
    responsable_contenido: ['management', 'my-global'],
    cliente: ['my-global'],
    external: ['my-global'],
  };

  runExternalUser(
    accessToken: string,
    refreshToken: string,
    idToken: string
  ): void {
    localStorage.setItem(LOCAL_STORAGE_EXT_TOKEN, accessToken);
    localStorage.setItem(LOCAL_STORAGE_TOKEN, accessToken);
    localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN, refreshToken);
    localStorage.setItem(LOCAL_STORAGE_ID_TOKEN, idToken);
    this.run();
  }

  setTokenAzure(
    accessToken: string,
    idTokenClaims: IdTokenClaims | undefined
  ): void {
    localStorage.setItem(LOCAL_STORAGE_INT_TOKEN, accessToken);
    localStorage.setItem(LOCAL_STORAGE_TOKEN, accessToken);
    if (idTokenClaims) {
      const idTokenClaimsString = JSON.stringify(idTokenClaims);
      localStorage.setItem(LOCAL_STORAGE_ID_TOKEN_CLAIMS, idTokenClaimsString);
    }
    this.run();
  }

  run(): void {
    this.token = localStorage.getItem(LOCAL_STORAGE_TOKEN) ?? '';
    this.refreshToken = localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN) ?? '';

    if (this.token) {
      const idToken = localStorage.getItem(LOCAL_STORAGE_ID_TOKEN);

      if (idToken) {
        this.userData = this.parseJwtToken(idToken);
      } else {
        const idTokenClaims = JSON.parse(
          localStorage.getItem(LOCAL_STORAGE_ID_TOKEN_CLAIMS) ?? '{}'
        );
        if (idTokenClaims && typeof idTokenClaims === 'object') {
          const { email = '', name = '', roles = [] } = idTokenClaims;
          this.userData = {
            email,
            name,
            roles,
            lang: 'es',
          } as UserSessionData;
        }
      }
    }

    this._isLoggedIn.next(this.hasValidToken());
  }

  setLoggedIn(value: boolean): void {
    this._isLoggedIn.next(value);
  }

  isValidRole(roles: Roles[]): boolean {
    const result =
      this.userData?.roles?.some((role) => roles.includes(role)) || false;

    return result;
  }

  canAccessTab(tab: tab): boolean {
    return this.userData?.roles?.some((role) => {
      const allowedTabs = this.roleTabAccess[role];
      return allowedTabs ? allowedTabs.includes(tab) : false;
    }) || false;
  }

  removeToken(): void {
    localStorage.removeItem(LOCAL_STORAGE_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_ID_TOKEN_CLAIMS);
    localStorage.removeItem(LOCAL_STORAGE_ID_TOKEN);
    // localStorage.removeItem(LOCAL_LANG);

    localStorage.removeItem(LOCAL_STORAGE_EXT_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_INT_TOKEN);

    // localStorage.clear();

    this.token = '';
    this.refreshToken = '';
    this.userData = null;

    this._isLoggedIn.next(this.hasValidToken());
    this._router.navigate([`${RoutePaths.login}`]);
  }

  private hasValidToken(): boolean {
    return !!localStorage.getItem(LOCAL_STORAGE_TOKEN);
  }

  logOutUser(): void {
    if(this.isValidRole(this.userExternalRole)) {
      this._spinnerService.show();
      this.getLogOut()
              .subscribe({
                next: (response) => {
                  this.removeToken();
                },
                error: (err) => {
                  this._modalService.showErrorModalService(() => {});
                  this._spinnerService.hide();
                },
                complete: () => {
                  this._spinnerService.hide();
                },
              });

    } else {
      this.logOutUserInter();
    }

  }

  logOutUserInter(): void {
    if (!this._msalService.instance) {
      this._router.navigate([`${RoutePaths.login}`, 'close']);
      return;
    }

    if (this._msalService.instance.getAllAccounts().length > 0) {
      this._msalService.instance.initialize().then(() => {
        this._msalService.logoutRedirect().subscribe({
          next: () => {
            this.removeToken();
          },
          error: (error) => {
            // this.removeToken();
            this._router.navigate([`${RoutePaths.login}`, 'close']);
            console.error('Logout failed', error);
          },
        });
      }).catch((error) => {
        this._router.navigate([`${RoutePaths.login}`, 'close']);
      });
    } else {
      // this.removeToken();
      this._router.navigate([`${RoutePaths.login}`, 'close']);
    }
  }

  parseJwtToken(idToken: string): UserSessionData {
    try {
      const decodedToken: any = jwtDecode(idToken);

      return {
        email: decodedToken.email,
        name: decodedToken.name,
        roles: decodedToken['custom:roles'] || ['external'],
        lang: decodedToken.locale || decodedToken['custom:language'],
      };
    } catch (error) {
      // console.error('Error al decodificar el token JWT:', error);
      return {};
    }
  }

  closeSesionAuto(): void {
    this._modalService.openGenericModal(
      this.closeAutoModalData(() => {
        this.logOutUser();
      }),
  );
  }

  showModalRecaptchaError(action: () => void): void {
    this._modalService.openGenericModal(
      this.recaptchaErrorModalData(() => {
        action();
      }),
  );
  }

  getLogOut = (): Observable<any> =>
    this._http.get<any>(`${this.baseUrl}/logout`);

  loadOneTrustScripts(lang: string): void {
    if (document.querySelector(`script[src*="${environment.onetrustscript}"]`)) {
      console.log('OneTrust scripts ya están cargados.');
      return;
    }

    // Cargar el primer script
    const script1 = document.createElement('script');
    script1.type = 'text/javascript';
    script1.src = `https://cdn.cookielaw.org/consent/${environment.onetrustscript}/OtAutoBlock.js`;
    document.head.appendChild(script1);

    // Cargar el segundo script
    const script2 = document.createElement('script');
    script2.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
    script2.type = 'text/javascript';
    script2.charset = 'UTF-8';
    script2.setAttribute('data-document-language', 'true');
    script2.setAttribute('data-language', lang || 'es');
    script2.setAttribute('data-domain-script', environment.onetrustscript);
    document.head.appendChild(script2);

    // Definir la función OptanonWrapper
    const script3 = document.createElement('script');
    script3.type = 'text/javascript';
    script3.text = 'function OptanonWrapper() { }';
    document.head.appendChild(script3);
  }
}
